/**
 * Dokumen penagihan yang dibawa kurir — "tukar faktur". MURNI.
 *
 * Istilahnya dari lapangan: kurir mengantar barang sekaligus membawa berkas penagihan, sebagian
 * DISERAHKAN ke pelanggan dan sebagian lagi harus KEMBALI dalam keadaan bertanda tangan. Berkas
 * yang harus kembali itulah yang menentukan apakah tagihan bisa diproses — tanpa tanda terima
 * bertanda tangan, penagihan berhenti meski barangnya sudah sampai.
 *
 * Karena itu satu-satunya angka yang berarti di layar adalah **berapa dari yang wajib kembali
 * sudah kembali**, bukan berapa dari seluruh berkas sudah ditandai. Menghitung dengan penyebut
 * "semua berkas" membuat tugas tampak 5/7 padahal seluruh berkas yang wajib kembali masih di
 * tangan kurir — angka yang menenangkan justru saat keadaannya paling perlu tindakan.
 */

export const DOCUMENT_TYPES = [
  'INVOICE',
  'FAKTUR_PAJAK',
  'SURAT_JALAN',
  'TANDA_TERIMA',
  'KUITANSI',
  'SURAT_KUASA',
  'BAST',
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

const LABELS: Readonly<Record<DocumentType, string>> = {
  INVOICE: 'Invoice',
  FAKTUR_PAJAK: 'Faktur Pajak',
  SURAT_JALAN: 'Surat Jalan',
  TANDA_TERIMA: 'Tanda Terima',
  KUITANSI: 'Kuitansi',
  SURAT_KUASA: 'Surat Kuasa',
  BAST: 'Berita Acara Serah Terima',
};

export function documentLabel(type: DocumentType): string {
  return LABELS[type];
}

/**
 * Arah bawaan tiap berkas — USULAN, bukan aturan.
 *
 * Owner memilih "manajer pilih per tugas" (26 Agu 2026), jadi setiap nilai di sini dapat diubah
 * di layar. Yang diberikan default hanyalah kebiasaan tukar faktur yang paling umum: berkas
 * bertanda tangan (surat jalan, tanda terima, BAST) kembali sebagai bukti, sisanya tinggal di
 * pelanggan sebagai dasar pembayaran.
 */
const DEFAULT_MUST_RETURN: Readonly<Record<DocumentType, boolean>> = {
  INVOICE: false,
  FAKTUR_PAJAK: false,
  SURAT_JALAN: true,
  TANDA_TERIMA: true,
  KUITANSI: false,
  SURAT_KUASA: false,
  BAST: true,
};

export interface ChecklistDraftRow {
  readonly documentType: DocumentType;
  /** Berkas ikut dibawa pada tugas ini. Tidak dicentang = tidak relevan, bukan "belum". */
  readonly selected: boolean;
  readonly mustReturn: boolean;
}

/** Draf awal: seluruh jenis ditampilkan, belum ada yang dipilih. */
export function emptyDraft(): readonly ChecklistDraftRow[] {
  return DOCUMENT_TYPES.map((documentType) => ({
    documentType,
    selected: false,
    mustReturn: DEFAULT_MUST_RETURN[documentType],
  }));
}

/**
 * Preset "tukar faktur lengkap" — ketujuh berkas sekaligus dengan arah bawaannya.
 *
 * ADA KARENA layar sebelumnya menuntut 14 ketukan (tujuh berkas × dua centang) untuk keadaan
 * yang hampir selalu sama. Pekerjaan yang sama setiap hari tidak boleh berbiaya sama setiap hari;
 * yang jarang berbeda cukup disediakan sebagai jalan kedua, bukan sebagai jalan satu-satunya.
 */
export function presetLengkap(): readonly ChecklistDraftRow[] {
  return DOCUMENT_TYPES.map((documentType) => ({
    documentType,
    selected: true,
    mustReturn: DEFAULT_MUST_RETURN[documentType],
  }));
}

/** Ringkasan satu baris untuk sebuah draf — dipakai label preset di layar. */
export function ringkasDraf(rows: readonly ChecklistDraftRow[]): string {
  const dipilih = rows.filter((r) => r.selected);
  if (dipilih.length === 0) return 'Tanpa dokumen';
  const kembali = dipilih.filter((r) => r.mustReturn).length;
  return `${dipilih.length} berkas, ${kembali} harus kembali`;
}

/**
 * Mencentang berkas mengembalikan arah bawaannya.
 *
 * Sengaja: manajer yang mencentang "Surat Jalan" hampir selalu memang menunggunya kembali.
 * Membiarkan arah pada nilai terakhir yang tak sengaja diubah membuat berkas wajib kembali
 * diam-diam berubah menjadi sekali jalan — dan kesalahan itu baru terasa saat penagihan.
 */
export function toggleSelected(
  rows: readonly ChecklistDraftRow[],
  documentType: DocumentType,
): readonly ChecklistDraftRow[] {
  return rows.map((row) => {
    if (row.documentType !== documentType) return row;
    const selected = !row.selected;
    return {
      ...row,
      selected,
      mustReturn: selected ? DEFAULT_MUST_RETURN[documentType] : row.mustReturn,
    };
  });
}

export function toggleMustReturn(
  rows: readonly ChecklistDraftRow[],
  documentType: DocumentType,
): readonly ChecklistDraftRow[] {
  return rows.map((row) =>
    row.documentType === documentType && row.selected
      ? { ...row, mustReturn: !row.mustReturn }
      : row,
  );
}

/** Isi yang dikirim ke server — hanya yang dicentang. */
export function checklistPayload(
  rows: readonly ChecklistDraftRow[],
): readonly { readonly documentType: DocumentType; readonly mustReturn: boolean }[] {
  return rows
    .filter((row) => row.selected)
    .map((row) => ({ documentType: row.documentType, mustReturn: row.mustReturn }));
}

// ── Bentuk yang datang dari server ──────────────────────────────────────────

/**
 * Menormalkan balasan `GET /field-tasks/:id/documents`.
 *
 * ADA KARENA bentuknya pernah salah ditebak dan akibatnya BUKAN pesan galat: server mengirim
 * `{ documents, state }`, klien mendeklarasikan array, lalu `summarize()` memanggil `.filter`
 * pada sebuah objek. Render melempar, dan tanpa error boundary seluruh aplikasi menjadi layar
 * putih tanpa satu kata pun — kurir dan manajer hanya melihat layar kosong (26 Agustus 2026).
 *
 * Karena itu fungsi ini TOLERAN pada kedua bentuk, seperti `normalizeMyAttendance`: server yang
 * berubah bentuk tidak boleh memutihkan layar. Yang tidak dikenal sama sekali menjadi daftar
 * kosong yang jujur, bukan lemparan — daftar kosong dapat ditampilkan, lemparan tidak.
 */
export function parseTaskDocuments(raw: unknown): readonly TaskDocument[] {
  if (Array.isArray(raw)) {
    return raw as readonly TaskDocument[];
  }
  if (typeof raw === 'object' && raw !== null) {
    const isi = (raw as { documents?: unknown }).documents;
    if (Array.isArray(isi)) {
      return isi as readonly TaskDocument[];
    }
  }
  return [];
}

// ── Keadaan checklist yang sudah tersimpan ──────────────────────────────────

export interface TaskDocument {
  readonly id: string;
  readonly documentType: DocumentType;
  readonly mustReturn: boolean;
  readonly carriedAt: string | null;
  readonly returnedAt: string | null;
}

export interface ChecklistSummary {
  readonly total: number;
  readonly carried: number;
  readonly mustReturnTotal: number;
  readonly returned: number;
  readonly complete: boolean;
  readonly pending: readonly DocumentType[];
}

/**
 * Ringkasan untuk ditampilkan. Cermin dari `checklistState` di server — bukan sumber kebenaran.
 *
 * Server tetap yang menegakkan urutan (tidak bisa "kembali" sebelum "dibawa"); di sini
 * perhitungan yang sama dipakai supaya kurir melihat angka yang sama dengan manajer.
 */
export function summarize(documents: readonly TaskDocument[]): ChecklistSummary {
  const wajibKembali = documents.filter((d) => d.mustReturn);
  const pending = wajibKembali.filter((d) => d.returnedAt === null).map((d) => d.documentType);

  return {
    total: documents.length,
    carried: documents.filter((d) => d.carriedAt !== null).length,
    mustReturnTotal: wajibKembali.length,
    returned: wajibKembali.filter((d) => d.returnedAt !== null).length,
    // Tugas tanpa berkas sama sekali bukan tugas yang "belum lengkap" — ia memang tanpa berkas.
    complete: documents.every((d) => d.carriedAt !== null) && pending.length === 0,
    pending,
  };
}

/**
 * Kalimat keadaan untuk satu berkas. Tiga keadaan, bukan dua — "dibawa" bukan akhir bagi berkas
 * yang wajib kembali, dan menyamakan keduanya menyembunyikan justru yang perlu ditagih.
 */
export function documentStatusText(document: TaskDocument): string {
  if (document.returnedAt !== null) return 'Sudah kembali';
  if (document.carriedAt !== null) {
    return document.mustReturn ? 'Dibawa — menunggu kembali' : 'Sudah diserahkan';
  }
  return 'Belum dibawa';
}

/** Langkah berikutnya yang boleh ditekan untuk satu berkas, atau `null` bila sudah selesai. */
export function nextAction(document: TaskDocument): 'carried' | 'returned' | null {
  if (document.carriedAt === null) return 'carried';
  if (document.mustReturn && document.returnedAt === null) return 'returned';
  return null;
}

/**
 * Peringatan sebelum tugas ditutup — kalimat, bukan daftar kode.
 *
 * Dibaca kurir di lapangan yang sedang terburu-buru; nama jenis dokumen disebut lengkap supaya
 * ia tahu berkas mana yang harus dicari, bukan sekadar bahwa "ada yang kurang".
 */
export function pendingWarning(summary: ChecklistSummary): string | null {
  if (summary.pending.length === 0) return null;
  const nama = summary.pending.map(documentLabel).join(', ');
  return `Belum kembali: ${nama}. Tagihan tidak dapat diproses sampai berkas ini diterima.`;
}
